import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { Box, Link, Paper, Alert, CssBaseline } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material";
import { verify } from "../../../utils/restAPI";
import { updateLoadingAxios } from "../../../Redux/Reducers/uiSlice";
import { useDispatch } from "react-redux";
import { lightAuthTheme, darkAuthTheme } from "../../../Theme/authTheme";
import AuthLogo from "../AuthLogo";
import { useTheme } from "../../../contexts/ThemeContext";

const EmailVerification = ({ email, username }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isDarkMode } = useTheme();
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(30);

  // Choose the appropriate theme based on current mode
  const authTheme = isDarkMode ? darkAuthTheme : lightAuthTheme;

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleVerification = async () => {
    if (!verificationCode.trim()) {
      setError("Please enter the verification code");
      return;
    }

    try {
      dispatch(updateLoadingAxios(true));
      const response = await verify(username, verificationCode);
      dispatch(updateLoadingAxios(false));

      if (response && response.success) {
        setIsVerified(true);
        setError("");
      } else {
        setError("Invalid verification code. Please try again.");
      }
    } catch (error) {
      dispatch(updateLoadingAxios(false));
      setError("Something went wrong. Please try again.");
    }
  };

  const handleResendCode = () => {
    setCountdown(30);
    setError("");
    // Here you would typically call an API to resend the verification code
    // For now, we'll just reset the countdown
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  if (isVerified) {
    return (
      <ThemeProvider theme={authTheme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: "100vh",
            backgroundColor: authTheme.palette.background.default,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem 1rem",
            zoom: 0.8,
          }}
        >
          <Container maxWidth="sm">
            <Paper
              elevation={0}
              sx={{
                padding: { xs: "2rem 1.5rem", sm: "3rem 2.5rem" },
                borderRadius: 3,
                backgroundColor: authTheme.palette.background.paper,
                boxShadow: authTheme.palette.mode === 'dark' 
                  ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
                  : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              }}
            >
              {/* Logo */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <AuthLogo />
              </Box>

              {/* Success Message */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem" },
                    fontWeight: 700,
                    color: authTheme.palette.text.primary,
                    mb: 2,
                  }}
                >
                  Email Verified!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: authTheme.palette.text.secondary,
                    fontSize: "1.125rem",
                    mb: 3,
                  }}
                >
                  Your email has been successfully verified. You can now sign in to your account.
                </Typography>
                <Alert severity="success" sx={{ borderRadius: 2, mb: 3 }}>
                  Email verification successful!
                </Alert>
              </Box>

              {/* Sign In Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleBackToLogin}
                sx={{
                  mb: 3,
                  height: "56px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  backgroundColor: authTheme.palette.mode === 'dark' ? "#F9FAFB" : "#1F2937",
                  color: authTheme.palette.mode === 'dark' ? "#111827" : "#FFFFFF",
                  "&:hover": {
                    backgroundColor: authTheme.palette.mode === 'dark' ? "#E5E7EB" : "#374151",
                  },
                }}
              >
                Sign In
              </Button>
            </Paper>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={authTheme}>
      <CssBaseline />
      <Box
        sx={{
          zoom: 0.75, 
          minHeight: "100vh",
          backgroundColor: authTheme.palette.background.default,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem 1rem",
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              padding: { xs: "2rem 1.5rem", sm: "3rem 2.5rem" },
              borderRadius: 3,
              backgroundColor: authTheme.palette.background.paper,
              boxShadow: authTheme.palette.mode === 'dark' 
                ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
                : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            {/* Logo */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <AuthLogo />
            </Box>

            {/* Header */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem" },
                  fontWeight: 700,
                  color: authTheme.palette.text.primary,
                  mb: 2,
                }}
              >
                Verify Your Email
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: authTheme.palette.text.secondary,
                  fontSize: "1.125rem",
                  mb: 2,
                }}
              >
                We've sent a verification code to:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: authTheme.palette.primary.main,
                  fontSize: "1.125rem",
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                {email}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: authTheme.palette.text.secondary,
                  fontSize: "1rem",
                }}
              >
                Enter the 6-digit code below to verify your email address.
              </Typography>
            </Box>

            {/* Form */}
            <Box sx={{ mt: 3 }}>
              {/* Error Message */}
              {error && (
                <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Verification Code Field */}
              <TextField
                fullWidth
                id="verificationCode"
                name="verificationCode"
                label="Verification Code"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                sx={{ mb: 3 }}
                inputProps={{
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                }}
              />

              {/* Verify Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleVerification}
                disabled={!verificationCode.trim()}
                sx={{
                  mb: 3,
                  height: "56px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  backgroundColor: authTheme.palette.mode === 'dark' ? "#F9FAFB" : "#1F2937",
                  color: authTheme.palette.mode === 'dark' ? "#111827" : "#FFFFFF",
                  "&:hover": {
                    backgroundColor: authTheme.palette.mode === 'dark' ? "#E5E7EB" : "#374151",
                  },
                  "&:disabled": {
                    backgroundColor: authTheme.palette.mode === 'dark' ? "#4B5563" : "#D1D5DB",
                    color: authTheme.palette.mode === 'dark' ? "#6B7280" : "#9CA3AF",
                  },
                }}
              >
                Verify Email
              </Button>

              {/* Resend Code Section */}
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="body2" sx={{ color: authTheme.palette.text.secondary, mb: 1 }}>
                  Didn't receive the code?
                </Typography>
                {countdown > 0 ? (
                  <Typography variant="body2" sx={{ color: authTheme.palette.text.disabled }}>
                    Resend available in {countdown} seconds
                  </Typography>
                ) : (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleResendCode}
                    sx={{
                      color: authTheme.palette.secondary.main,
                      textDecoration: "none",
                      fontWeight: 600,
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Resend Code
                  </Link>
                )}
              </Box>

              {/* Back to Login Link */}
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: authTheme.palette.text.secondary }}>
                  Already verified?{" "}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleBackToLogin}
                    sx={{
                      color: authTheme.palette.secondary.main,
                      textDecoration: "none",
                      fontWeight: 600,
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Back to Login
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default EmailVerification;
